const defaultOptions = {
    partSize: 5 * 1024 * 1024 * 1024,
    parallelism: 3,
    retryAttempts: 0,
  };
  
  export interface S3MultipartType {
    options: S3MultipartOptions;
    createUpload: any;
    getPartUrl: any;
    completeUpload: any;
  }
  
  export interface S3MultipartOptions {
    partSize: number;
    parallelism: number;
    retryAttempts: number;
    onProgress: any;
  }
  
  export default class S3Multipart implements S3MultipartType {
    options: S3MultipartOptions = null;
    createUpload: any = null;
    getPartUrl: any = null;
    completeUpload: any = null;
  
    constructor(options) {
      this.options = Object.assign({}, defaultOptions, options);
      this.createUpload = options.createUpload;
      this.getPartUrl = options.getPartUrl;
      this.completeUpload = options.completeUpload;
  
      if (!this.createUpload || !this.getPartUrl || !this.completeUpload) {
        throw new Error('Incomplete options, missing createUpload/getPartUrl/completeUpload');
      }
    }
  
    upload(file) {
      return this.createUpload(file).then((uploadId) => {
        const totalParts = Math.ceil(file.size / this.options.partSize);
        const etags = new Array(totalParts);
        const activeXhr = [];
        let partNumber = 0;
        let completedParts = 0;
        let totalSentBytes = 0;
  
        const onProgress = (sentBytes) => {
          totalSentBytes += sentBytes;
          // a hack to make sure the percent upload doesn't exceed 100%
          if (totalSentBytes > file.size) {
            totalSentBytes = file.size;
          }
          if (this.options.onProgress) {
            this.options.onProgress(totalSentBytes, file.size);
          }
        };
  
        return new Promise<void>((resolve, reject) => {
          const nextPart = (currentPartNumber, attemptsLeft) => {
            // console.log('nextPart', { completedParts, totalParts, partNumber, currentPartNumber, attemptsLeft });
            if (partNumber > totalParts) {
              if (completedParts === totalParts) {
                return resolve();
              }
            } else {
              const myAttemptsLeft = attemptsLeft;
              const [xhr, promise] = this.sendPart(uploadId, file, currentPartNumber, onProgress, attemptsLeft);
              activeXhr.push(xhr);
  
              promise
                .then((etag) => (etags[currentPartNumber] = etag))
                .then(() => {
                  completedParts++;
                  const xhrIndex = activeXhr.indexOf(xhr);
                  activeXhr.splice(xhrIndex, 1);
  
                  const nextPartNumber = ++partNumber;
                  const newAttemptsLeft = Object.assign({}, this.options).retryAttempts;
                  nextPart(nextPartNumber, newAttemptsLeft);
                })
                .catch((err) => {
                  const xhrIndex = activeXhr.indexOf(xhr);
                  activeXhr.splice(xhrIndex, 1);
                  if (attemptsLeft > 0) {
                    const newAttemptsLeft = myAttemptsLeft - 1;
                    nextPart(currentPartNumber, newAttemptsLeft);
                  } else {
                    reject(err);
                  }
                });
            }
          };
  
          for (let i = 0; i < this.options.parallelism; i++) {
            const nextPartNumber = ++partNumber;
            const attemptsLeft = Object.assign({}, this.options).retryAttempts;
            nextPart(nextPartNumber, attemptsLeft);
          }
        })
          .then(() => {
            return this.completeUpload(file, uploadId, etags);
          })
          .catch((err) => {
            for (let i = 0; i < activeXhr.length; i++) {
              const xhr = activeXhr[i];
              xhr.abort();
            }
            throw err;
          });
      });
    }
  
    sendPart(uploadId, file, partNumber, onProgress, retryAttempt) {
      // console.log('sendPart', { uploadId, file, partNumber, onProgress, retryAttempt });
      const { partSize } = this.options;
      const partStart = (partNumber - 1) * partSize;
      const part = file.slice(partStart, partStart + partSize);
      const request = new XMLHttpRequest();
      let myUploadId = uploadId;
      // if (partNumber === 2 && retryAttempt > 1) {
      //   // console.log('sendPart forcing fail', { partNumber, retryAttempt });
      //   myUploadId = 'a';
      // }
      return [
        request,
        this.getPartUrl(file, myUploadId, partNumber, part.size).then((partUrl) => {
          return new Promise((resolve, reject) => {
            let isGood = true;
            request.onreadystatechange = function() {
              // const readyState = request.readyState;
              // const status = requests.status;
              // console.log('onreadystatechange', { readyState, status });
              if (request.readyState === 4) {
                if (request.status !== 200) {
                  isGood = false;
                  reject(new Error('Unexpected response HTTP ' + request.status + ' ' + request.statusText));
                } else {
                  resolve(request.getResponseHeader('ETag'));
                }
              }
            };
  
            let lastLoaded = 0;
            request.upload.onprogress = function(e) {
              const { loaded } = e;
              const totalProgress = loaded - lastLoaded;
              // console.log('onprogress', { isGood, totalProgress, lastLoaded, loaded });
              if (isGood) {
                onProgress(loaded - lastLoaded);
              }
              lastLoaded = loaded;
            };
  
            request.open('PUT', partUrl, true);
            request.setRequestHeader('Content-Type', '');
            request.send(part);
          });
        }),
      ];
    }
  }
  